// Ein Knoten im Baum der Seitenleiste -- das, was TreeNode zeichnet
// (docs/shell.md). Ohne Stores: auch das Recovery-Tool zeichnet damit
// seinen Baum (docs/recovery-tool-ux.md, „Gestaltung"). WAS im Baum des
// Dashboards steht, baut tree.svelte.ts.

import type { IconName } from "../Icon.svelte";
import { Scharf } from "../scharf.svelte";

export type ZeilenStatus = "ok" | "warn" | "bad" | "off" | "laeuft";

export interface BaumKnoten {
  id: string;
  label: string;
  /** Ziel einer Seite (nur Seiten-Knoten). Die Telefon-Übersicht
   *  verlinkt damit, ohne den Shell-Weg (oeffnen) zu gehen. */
  href?: string;
  icon?: IconName;
  /** Rechtsbuendig: eine Zahl, ein Zustand, eine Zeitangabe. */
  tail?: string;
  status?: ZeilenStatus;
  /** Läuft hier gerade etwas mit Fortschritt (0..100): die Zeile füllt
   *  sich von links, statt eines Punkts (lib/laeuft.svelte.ts). */
  fortschritt?: number;
  /** Einfachklick: als VORSCHAU zeigen (ersetzt die vorige Vorschau). */
  oeffnen: () => void;
  /** Doppelklick: dauerhaft anheften. */
  anheften: () => void;
  /** Ist das zugehoerige Ding gerade als Tab offen? */
  offen: boolean;
  /** Ist es der Tab mit Fokus -- die Seite, auf der man steht? Pflicht
   *  statt optional: wer einen Knoten baut, soll sich entscheiden
   *  muessen. Bis 23.09. sah die aktive Seite im Baum aus wie jede
   *  andere offene; es gab keinen Ort, der "hier" sagte. */
  aktiv: boolean;
  /** Offene Punkte AN DIESEM Knoten -- Dinge, die auf einen Handgriff
   *  warten (Neustart faellig, Updates verfuegbar, Aufgabe haengt).
   *  Bewusst NICHT "Status": ein Pool bei 79 % ist voll, aber er wartet
   *  nicht auf mich. Was hier zaehlt, kostet einen Handgriff. */
  punkte?: number;
  punkteTon?: "warn" | "bad";
  /** Summe aus eigenen Punkten und denen aller Kinder. Setzt
   *  zaehleHoch(), damit ein zugeklappter Ast nichts verbirgt. */
  summe?: number;
  /** Der SCHWERSTE Ton im Ast (eigener Punkt oder Kind). Ohne den
   *  faellt die Stufe beim Hochzaehlen unter den Tisch: ein rotes
   *  Kind unter einem zugeklappten Knoten war bis 06.09. nur eine
   *  gelbe Zahl -- und in der Aktivitaetsleiste gar keine Farbe. */
  summeTon?: "warn" | "bad";
  /** Unterknoten. Leer/undefined = kein Aufklapp-Dreieck. */
  kinder?: BaumKnoten[];
  /** Beim ersten Anzeigen aufgeklappt. */
  standardOffen?: boolean;
  /** Ein Handgriff an der Zeile, als Symbol beim Darüberfahren statt des
   *  `tail` (docs/gestaltung.md Regel 5). Mit `scharf` braucht er die
   *  zweite Geste: der erste Klick färbt ihn rot, der zweite löst aus
   *  (baumScharf, Muster „Scharf schalten"). */
  /** Kein Zustandspunkt links, auch wenn `status` gesetzt ist: wenn der
   *  `tail` den Zustand schon in seiner Farbe sagt (ein Chat „wartet",
   *  dazu die Zähl-Marke) -- Tim 16.10.2026: „Der Punkt ganz links kann
   *  auch noch weg". */
  ohnePunkt?: boolean;
  aktion?: BaumAktion;
  /** Mehrere Handgriffe (Claude: Ins Archiv, Wegräumen); `aktion` ist der
   *  Fall mit einem. Höchstens einer davon `scharf`. */
  aktionen?: BaumAktion[];
}

export interface BaumAktion {
  icon: IconName;
  label: string;
  scharf?: string;
  ausfuehren: () => void;
}

/** Der eine Scharf-Zustand des Baums: höchstens eine Zeile ist scharf. */
export const baumScharf = new Scharf();

/** hatAktives: steht man auf einem Knoten in diesem Ast? Ein zugeklappter
 *  Ast zeigt das an seiner eigenen Zeile (TreeNode) -- sonst sagte der Baum
 *  nirgends „hier", sobald die markierte Zeile nicht zu sehen ist. */
export function hatAktives(knoten: BaumKnoten[]): boolean {
  return knoten.some((k) => k.aktiv || (k.kinder?.length ? hatAktives(k.kinder) : false));
}

/** Was TreeNode vom Sortiermodus einer Ebene braucht. Im Dashboard ist das
 *  BaumOrdnen (tree.svelte.ts, gemerkt in den Einstellungen); wer nicht
 *  sortieren lässt, gibt `ebene: null` und tut beim Verschieben nichts. */
export interface BaumOrdner {
  /** Ebene im Sortiermodus (Kennung des Elternknotens, "" = oberste), sonst null */
  ebene: string | null;
  /** Zieh-Gruppe der Ebene (press-reorder: data-reorder-group) */
  gruppe(eltern: string): string;
  verschiebe(eltern: string, aktuell: string[], von: string, nach: string): void;
}
